import areaData from 'china-area-data'

const REGION_DATA = areaData?.default || areaData?.['module.exports'] || areaData || {}

const provinceSource = REGION_DATA['86'] || {}

/**
 * 构建 China Area Data 的名称级联选项。
 * @returns {Array<object>} 名称级联选项
 */
export const chinaAreaOptions = Object.keys(provinceSource).map((provinceCode) => {
  const citySource = REGION_DATA[provinceCode] || {}
  return {
    value: provinceSource[provinceCode],
    label: provinceSource[provinceCode],
    children: Object.keys(citySource).map((cityCode) => {
      const districtSource = REGION_DATA[cityCode] || {}
      return {
        value: citySource[cityCode],
        label: citySource[cityCode],
        children: Object.keys(districtSource).map((districtCode) => ({
          value: districtSource[districtCode],
          label: districtSource[districtCode]
        }))
      }
    })
  }
})

/**
 * 构建保留行政区划编码的省市区级联选项。
 * @returns {Array<object>} 编码级联选项
 */
export const chinaAreaCodeOptions = Object.keys(provinceSource).map((provinceCode) => {
  const citySource = REGION_DATA[provinceCode] || {}
  return {
    value: provinceCode,
    label: provinceSource[provinceCode],
    children: Object.keys(citySource).map((cityCode) => {
      const districtSource = REGION_DATA[cityCode] || {}
      return {
        value: cityCode,
        label: citySource[cityCode],
        children: Object.keys(districtSource).map((districtCode) => ({
          value: districtCode,
          label: districtSource[districtCode]
        }))
      }
    })
  }
})

/**
 * 将行政区划编码转换为展示名称；无法识别的编码不向前端展示。
 * @param {string|number} code 行政区划编码
 * @returns {string} 行政区划名称
 */
export function getChinaAreaName(code) {
  if (!code) return ''
  const option = chinaAreaCodeOptions.find(province => province.value === String(code))
  if (option) return option.label
  for (const province of chinaAreaCodeOptions) {
    const city = (province.children || []).find(item => item.value === String(code))
    if (city) return city.label
    const district = (province.children || []).flatMap(item => item.children || []).find(item => item.value === String(code))
    if (district) return district.label
  }
  return ''
}

/**
 * 兼容历史名称数据，将省市区字段统一转换为行政区划编码。
 * @param {{province?:string,city?:string,district?:string}} address 地址快照
 * @returns {{province:string,city:string,district:string}} 可识别的行政区划编码
 */
export function normalizeChinaAreaCodes(address) {
  const provinceValue = String(address?.province || '')
  const province = chinaAreaCodeOptions.find(
    item => item.value === provinceValue || item.label === provinceValue
  )
  const provinceName = province?.label || getChinaAreaName(provinceValue) || provinceValue
  const cityValue = String(address?.city || '')
  const city = (province?.children || []).find(
    item => item.value === cityValue || item.label === cityValue
  )
  const cityName = city?.label || getChinaAreaName(cityValue) || cityValue
  const districtValue = String(address?.district || '')
  const district = (city?.children || []).find(
    item => item.value === districtValue || item.label === districtValue
  )
  return {
    province: province?.value || '',
    city: city?.value || '',
    district: district?.value || ''
  }
}

/**
 * 将地址编码格式化为仅包含名称的完整地址，兼容历史名称数据。
 * @param {{province?:string,city?:string,district?:string,addressDetail?:string}} address 地址对象
 * @returns {string} 省市区名称加详细地址
 */
export function formatChinaAreaAddress(address) {
  const codes = normalizeChinaAreaCodes(address)
  const displayName = (code, fallback) => {
    const name = getChinaAreaName(code)
    if (name && name !== '市辖区') return name
    return fallback && !/^\d{6}$/.test(fallback) ? fallback : ''
  }
  return [
    displayName(codes.province, address?.province),
    displayName(codes.city, address?.city),
    displayName(codes.district, address?.district),
    address?.addressDetail
  ].filter(Boolean).join(' ')
}

/**
 * 根据县级行政区划编码生成完整区域名称。
 * @param {string|number} regionCode 县级行政区划编码
 * @param {string} fallbackName 后端区域名称快照
 * @returns {string} 省/市/区名称
 */
export function formatChinaRegionName(regionCode, fallbackName = '') {
  const code = String(regionCode || '')
  for (const province of chinaAreaCodeOptions) {
    for (const city of province.children || []) {
      const district = (city.children || []).find(item => item.value === code)
      if (district) {
        return [province.label, city.label, district.label].filter(label => label && label !== '市辖区').join(' / ')
      }
    }
  }
  return fallbackName || ''
}
